import React from "react";
import dayjs from "dayjs";
import {
  Box, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader,
  DrawerOverlay, Flex, Icon, Link, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdOpenInNew } from "react-icons/md";
import ReservationStatusChip from "./ReservationStatusChip";
import { eventTypeLabel } from "constants/eventTypeLabels";
import { pickArtistLabel, pickAuditorLabel } from "./reservationsManageConstants";
import { KV_ROW } from "./reservationsManageStyles";
import { buildMapsUrl, formatCoordinates, hasCoordinates } from "tools/geo";

/**
 * Formats an ISO date as DD/MM/YYYY HH:mm or "—" when missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDateTime(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Renders a labeled key/value row used across detail sections.
 * @param {Object} props - label (string), value (node)
 * @returns {React.ReactElement}
 */
function KvRow({ label, value }) {
  const keyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  return (
    <Flex {...KV_ROW}>
      <Text fontSize="xs" color={keyColor}>{label}</Text>
      <Text fontSize="sm" fontWeight="600" color={valColor} textAlign="right" wordBreak="break-word">{value || "—"}</Text>
    </Flex>
  );
}

/**
 * Renders an uppercase section title above a group of key/value rows.
 * @param {Object} props - children (label node)
 * @returns {React.ReactElement}
 */
function SectionTitle({ children }) {
  const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Text fontSize="xs" fontWeight="700" letterSpacing="0.5px" textTransform="uppercase" color={color} mt="16px" mb="6px">
      {children}
    </Text>
  );
}

/**
 * Drawer header showing the status chip and the reservation / service IDs.
 * @param {Object} props - reservation
 * @returns {React.ReactElement}
 */
function HeaderBlock({ reservation }) {
  const idColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Box>
      <ReservationStatusChip status={reservation?.status} />
      <Text fontSize="xs" fontFamily="mono" color={idColor} mt="8px">
        Réservation #{reservation?.id || "—"} · Service #{reservation?.services_id || "—"}
      </Text>
    </Box>
  );
}

/**
 * Event section: title + event type + date + venue (address/city/country).
 * @param {Object} props - reservation
 * @returns {React.ReactElement}
 */
function EventSection({ reservation }) {
  const descColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
  const descBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  const address = [reservation?.address, reservation?.city, reservation?.postal_code, reservation?.country].filter(Boolean).join(", ");
  return (
    <Box>
      <SectionTitle>Événement</SectionTitle>
      <KvRow label="Titre" value={reservation?.title} />
      <KvRow label="Type" value={eventTypeLabel(reservation?.event)} />
      <KvRow label="Date" value={formatDateTime(reservation?.event_date)} />
      <KvRow label="Adresse" value={address} />
      {hasCoordinates(reservation) && (
        <KvRow
          label="Coordonnées GPS"
          value={(
            <Link href={buildMapsUrl(reservation.latitude, reservation.longitude)} isExternal display="inline-flex" alignItems="center" gap="4px" color="brand.500">
              {formatCoordinates(reservation.latitude, reservation.longitude)}
              <Icon as={MdOpenInNew} w="14px" h="14px" />
            </Link>
          )}
        />
      )}
      <KvRow label="Téléphone" value={reservation?.phone} />
      {reservation?.description && (
        <Box mt="8px" p="10px" borderRadius="10px" bg={descBg}>
          <Text fontSize="sm" color={descColor} whiteSpace="pre-wrap">{reservation.description}</Text>
        </Box>
      )}
    </Box>
  );
}

/**
 * Parties section: artist and auditor identities.
 * @param {Object} props - reservation
 * @returns {React.ReactElement}
 */
function PartiesSection({ reservation }) {
  const auditor = pickAuditorLabel(reservation);
  return (
    <Box>
      <SectionTitle>Parties prenantes</SectionTitle>
      <KvRow label="Artiste" value={pickArtistLabel(reservation)} />
      <KvRow label="Artiste email" value={reservation?.artist?.email} />
      <KvRow label="Auditor" value={auditor.name} />
      <KvRow label="Auditor email" value={auditor.email} />
      {reservation?.entity_id && <KvRow label="Entité (organisateur)" value={`#${reservation.entity_id}`} />}
    </Box>
  );
}

/**
 * Financial section: amounts, contact fee, computed total, refund policy and the Stripe invoice link.
 * Reservation total = total_amount + travel_expenses + contact_fee.
 * @param {Object} props - reservation
 * @returns {React.ReactElement}
 */
function PaymentSection({ reservation }) {
  const total = (Number(reservation?.total_amount) || 0)
    + (Number(reservation?.travel_expenses) || 0)
    + (Number(reservation?.contact_fee) || 0);
  return (
    <Box>
      <SectionTitle>Finance</SectionTitle>
      <KvRow label="Montant prestation" value={reservation?.total_amount != null ? reservation.total_amount : null} />
      <KvRow label="Frais déplacement" value={reservation?.travel_expenses != null ? reservation.travel_expenses : null} />
      <KvRow label="Frais de contact" value={reservation?.contact_fee != null ? reservation.contact_fee : null} />
      <KvRow label="Total réservation" value={total} />
      <KvRow label="Politique remboursement" value={reservation?.refund_policy} />
      <KvRow label="Référence paiement" value={reservation?.payment_ref} />
      <KvRow label="Contrat" value={reservation?.has_contract ? "✓ Présent" : "—"} />
      <KvRow label="Expire transfert" value={formatDateTime(reservation?.transfer_expires_at)} />
      {reservation?.invoice && (
        <Link href={reservation.invoice} isExternal display="inline-flex" alignItems="center" gap="4px" color="brand.500" fontSize="sm" mt="8px">
          <Icon as={MdOpenInNew} w="14px" h="14px" /> Voir la facture
        </Link>
      )}
    </Box>
  );
}

/**
 * Service section: linked service summary (id + country + price + duration + themes).
 * @param {Object} props - reservation
 * @returns {React.ReactElement}
 */
function ServiceSection({ reservation }) {
  const s = reservation?.service || {};
  const themes = Array.isArray(s.thematics) ? s.thematics.join(" · ") : "";
  const duration = s.duration_of_the_service != null ? `${s.duration_of_the_service} ${s.unit_duration_of_the_service || ""}`.trim() : null;
  const prep = s.preparation_time != null ? `${s.preparation_time} ${s.unit_of_the_preparation_time || ""}`.trim() : null;
  return (
    <Box>
      <SectionTitle>Service réservé</SectionTitle>
      <KvRow label="ID" value={s.id ? `#${s.id}` : null} />
      <KvRow label="Pays" value={s.country} />
      <KvRow label="Prix unitaire" value={s.price != null ? s.price : null} />
      <KvRow label="Durée" value={duration} />
      <KvRow label="Préparation" value={prep} />
      <KvRow label="Thématiques" value={themes} />
    </Box>
  );
}

/**
 * Right-side drawer showing the full detail of a reservation.
 * @param {Object} props - reservation (null = closed), onClose
 * @returns {React.ReactElement}
 */
function ReservationDetailDrawer({ reservation, onClose }) {
  return (
    <Drawer isOpen={Boolean(reservation)} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{reservation && <HeaderBlock reservation={reservation} />}</DrawerHeader>
        <DrawerBody>
          {reservation && (
            <Box>
              <EventSection reservation={reservation} />
              <PartiesSection reservation={reservation} />
              <PaymentSection reservation={reservation} />
              <ServiceSection reservation={reservation} />
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default ReservationDetailDrawer;
